import React, { useEffect } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { spaces, palette, fontWeights } from '../../../assets/styles/themes/tokens';
import { selectUser } from '../../../reducers/user.selectors';
import AIAssistantFooter from '../shared/Footer/AIAssistantFooter';
import AIAssistantTemplatePromptList, {
  TemplatePromptItem,
} from '../shared/TemplatePromptForm/AIAssistantTemplatePromptList';
import { getStartPageActionCards } from '../shared/utils';
import useTemplateSelection from '../shared/useTemplateSelection';
import useScreenSize from '../../../utils/useScreenSize';
import services from '../../../services';
import { getAIAssistantPageSource } from '../../../utils/getAIAssistantPageSource';

const MAX_WIDTH_LARGE = '1176px';
const MAX_WIDTH_CONTAINER = '1440px';

// Get dynamic greeting based on time of day
const getGreetingByTime = (): string => {
  const hour = new Date().getHours();
  if (hour >= 0 && hour < 12) return 'What\'s on the agenda today {{name}}?';
  if (hour >= 12 && hour < 18) return 'Hi {{name}}! Let\'s continue!';
  return 'Hello {{name}}! Anything still open?';
};

const getMainContainerPadding = (isXsScreen: boolean, isSmScreen: boolean, isMdScreen: boolean) => {
  if (isXsScreen) return `${spaces.lg} ${spaces.md}`;
  if (isSmScreen) return spaces.lg;
  if (isMdScreen) return `${spaces.lg} ${spaces.xl}`;
  return spaces.xl;
};

const getMainContainerAlignItems = (isXsScreen: boolean, isSmScreen: boolean) => {
  if (isXsScreen) return 'flex-start';
  if (isSmScreen) return 'flex-start';
  return 'center';
};

const getMainContainerGap = (isXsScreen: boolean) => {
  if (isXsScreen) return spaces.xl;
  return spaces.xxxxl;
};

const getPromptSectionPadding = (isXsScreen: boolean, isSmScreen: boolean) => {
  if (isXsScreen) return 0;
  if (isSmScreen) return `0 ${spaces.xxl}`;
  return `0 ${spaces.xxxxxl}`;
};

const getPromptSectionMaxWidth = (isSmScreen: boolean, isMdScreen: boolean) => {
  if (isSmScreen) return undefined;
  if (isMdScreen) return MAX_WIDTH_LARGE;
  return undefined;
};

const getWelcomeSectionPaddingTop = (isXsScreen: boolean) => {
  if (isXsScreen) return 0;
  return spaces.xxxl;
};

const getTemplateGridProps = (isXsScreen: boolean) => {
  if (isXsScreen) {
    return {
      xs: 6,
      sm: 6,
      md: 6,
    };
  }
  return {
    xs: 12,
    sm: 3,
    md: 3,
  };
};

const headingTypographyStyles = {
  color: palette.text.primary,
  textAlign: 'center',
};

const promptInputWrapperStyles = {
  width: '100%',
};

const templateSectionStyles = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  alignSelf: 'stretch',
};

const templateTitleStyles = {
  color: palette.text.primary,
  fontWeight: fontWeights.medium,
};

const AIToolsPageContent: React.FC = () => {
  const user = useSelector(selectUser);
  const { t } = useTranslation();
  const { isXsScreen, isSmScreen, isMdScreen } = useScreenSize();
  const { handleTemplateClick } = useTemplateSelection();
  const templateItems: TemplatePromptItem[] = getStartPageActionCards(user);

  const mainContainerStyles = {
    display: 'flex',
    maxWidth: MAX_WIDTH_CONTAINER,
    padding: getMainContainerPadding(isXsScreen, isSmScreen, isMdScreen),
    flexDirection: 'column',
    alignItems: getMainContainerAlignItems(isXsScreen, isSmScreen),
    gap: getMainContainerGap(isXsScreen),
    flex: '1 0 0',
    alignSelf: 'stretch',
    marginLeft: 'auto',
    marginRight: 'auto',
  };

  const getPromptSectionStyles = () => {
    const maxWidth = getPromptSectionMaxWidth(isSmScreen, isMdScreen);
    return {
      display: 'flex',
      ...(maxWidth && { maxWidth }),
      padding: getPromptSectionPadding(isXsScreen, isSmScreen),
      flexDirection: 'column',
      alignItems: 'center',
      gap: spaces.md,
      alignSelf: 'stretch',
    };
  };

  const promptSectionStyles = getPromptSectionStyles();

  const welcomeSectionStyles = {
    display: 'flex',
    paddingTop: getWelcomeSectionPaddingTop(isXsScreen),
    flexDirection: 'column',
    alignItems: 'center',
    alignSelf: 'stretch',
  };

  useEffect(() => {
    services
      .track
      .ampliTrackAIAssistantOpenedEvent({
        pageSource: getAIAssistantPageSource(window.location.pathname),
      });
  }, []);

  return (
    <Box sx={mainContainerStyles}>
      <Box sx={promptSectionStyles}>
        <Stack spacing={spaces.xs} sx={welcomeSectionStyles}>
          <Typography variant='h5' sx={headingTypographyStyles}>
            {t(getGreetingByTime(), { name: user?.firstName })}
          </Typography>
        </Stack>

        <Box sx={promptInputWrapperStyles}>
          <AIAssistantFooter/>
        </Box>
      </Box>

      <Box sx={templateSectionStyles}>
        <Typography variant='subtitle1' sx={templateTitleStyles}>
          {t('Start with a template')}
        </Typography>
        <AIAssistantTemplatePromptList
          items={templateItems}
          isExtended={true}
          onPromptClick={handleTemplateClick}
          gridItemProps={getTemplateGridProps(isXsScreen)}
        />
      </Box>
    </Box>
  );
};

export default AIToolsPageContent;

